/**
 * Xtream Codes player_api.php client.
 *
 * Most IPTV providers run on the Xtream Codes panel. When the playlist URL
 * looks like `http://host:port/get.php?username=X&password=Y&type=m3u_plus…`
 * we can hit the companion `player_api.php` endpoint for rich metadata —
 * proper posters, plot, cast, year, rating, structured seasons + episodes —
 * instead of guessing everything from filenames.
 *
 * All requests use the host-side fetch (Electron IPC / Capacitor / browser).
 */
import { webFetch } from "./webFetch";

// Yields to the event loop every N iterations so a big enrichment /
// harvest pass doesn't block the UI thread on Smart-TV WebViews.
const YIELD_EVERY = 5000;


/**
 * Parse Xtream creds out of an M3U URL.
 * Returns `null` if the URL doesn't look like an Xtream playlist.
 */
export function parseXtreamCreds(m3uUrl) {
  if (!m3uUrl) return null;
  let u;
  try {
    u = new URL(m3uUrl);
  } catch {
    return null;
  }
  // Most Xtream panels expose /get.php or /xmltv.php with username + password.
  if (!/\/(get|xmltv|player_api)\.php$/i.test(u.pathname)) return null;
  const username = u.searchParams.get("username");
  const password = u.searchParams.get("password");
  if (!username || !password) return null;
  return {
    base: `${u.origin}/player_api.php`,
    streamBase: u.origin,
    username,
    password,
  };
}

/**
 * Default fetcher used when callers don't pass one explicitly. Routes
 * through Electron IPC (CORS-free) on desktop, falls back to `fetch()`
 * on Capacitor / web. Mirrors the logic in `playlistCache.rawFetchText`
 * but kept local here so callers that only need a single API call
 * (e.g. Browse.jsx's per-series episode fetch) don't have to thread
 * a fetch function through.
 *
 * v1.3.66 — added because `Browse.jsx` was calling `fetchSeriesInfo`
 * without a `fetchFn` → silent crash inside `call()` → episodes never
 * loaded from the Xtream JSON API → user reports "S2E295–299 missing"
 * because the M3U parser is silently dropping the tail of huge
 * playlists. The Xtream `get_series_info` endpoint returns the full
 * episode list directly from the provider's database, bypassing the
 * M3U parser entirely.
 */
async function _defaultFetchText(url) {
  if (typeof window !== "undefined" && window.electronAPI?.fetchUrl) {
    const r = await window.electronAPI.fetchUrl(url);
    if (!r?.ok) throw new Error(r?.error || `HTTP ${r?.status || "?"}`);
    return r.body;
  }
  // v1.4.8 — Route via backend proxy when in Web Player mode so CORS
  // doesn't kill Xtream API calls in browsers. Native builds keep
  // using plain fetch (no behavior change).
  const r = await webFetch(url, { cache: "no-store" });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return await r.text();
}

/**
 * Run one player_api.php query. `fetchFn(url) -> Promise<text>` lets the
 * caller route requests through Electron IPC (CORS-free) on desktop.
 * When not provided, falls back to `_defaultFetchText`.
 */
async function call(creds, action, extra, fetchFn) {
  const url = new URL(creds.base);
  url.searchParams.set("username", creds.username);
  url.searchParams.set("password", creds.password);
  if (action) url.searchParams.set("action", action);
  for (const [k, v] of Object.entries(extra || {})) {
    url.searchParams.set(k, String(v));
  }
  // v1.3.31 — cache-buster so the WebView / any intermediate proxy
  // never serves a stale catalog. Previously a manual "Reload
  // Playlist" silently re-used yesterday's get_series response,
  // hiding any newly-added episodes from the provider.
  url.searchParams.set("_t", String(Date.now()));
  const f = fetchFn || _defaultFetchText;
  const text = await f(url.toString());
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * Pull all the bulk endpoints in parallel.
 * Returns null if the panel doesn't respond like Xtream.
 *
 * Uses Promise.allSettled so a single failing endpoint (e.g. get_vod_streams
 * times out on a huge provider, or get_live_streams 503s) never wipes out
 * the data that DID arrive. Previously a single failure caused the whole
 * catalog to come back null — which in turn killed the series harvest and
 * made ZA Player Pro look like it discovered fewer series than vupro.apk.
 *
 * `get_series` is retried once because it's the largest payload on most
 * panels and the single most important endpoint for the Series tab.
 */
export async function fetchXtreamCatalog(creds, fetchFn) {
  if (!creds) return null;
  const safeCall = (action) =>
    call(creds, action, {}, fetchFn).catch(() => null);

  const results = await Promise.allSettled([
    safeCall(""), // user info / server info
    safeCall("get_live_categories"),
    safeCall("get_vod_categories"),
    safeCall("get_series_categories"),
    safeCall("get_vod_streams"),
    safeCall("get_series"),
    safeCall("get_live_streams"),
  ]);
  const pick = (i) =>
    results[i].status === "fulfilled" ? results[i].value : null;

  const info = pick(0);
  const liveCats = pick(1);
  const vodCats = pick(2);
  const serCats = pick(3);
  const vodStreams = pick(4);
  let serList = pick(5);
  const liveStreams = pick(6);

  // Single retry for get_series — it's the largest payload on most
  // panels and routinely 503s on first call. vupro.apk does the same
  // retry which is why it can show "more" series than we did.
  if (!Array.isArray(serList)) {
    serList = await safeCall("get_series");
  }

  if (
    !Array.isArray(vodStreams) &&
    !Array.isArray(liveStreams) &&
    !Array.isArray(serList)
  ) {
    return null;
  }

  // v1.3.45 — Single log line emitted at the call site in
  // playlistCache.js (`[xtream] catalog live=… vod=… series=…`).
  // An earlier copy used to live here too and showed up as a
  // duplicate in the on-screen debug overlay.

  return {
    info: info && info.user_info ? info : null,
    categories: {
      live: indexBy(liveCats || [], "category_id"),
      vod: indexBy(vodCats || [], "category_id"),
      series: indexBy(serCats || [], "category_id"),
    },
    liveStreams: liveStreams || [],
    vodStreams: vodStreams || [],
    seriesList: serList || [],
  };
}

function indexBy(arr, key) {
  const out = new Map();
  for (const item of arr || []) {
    if (item && item[key] != null) out.set(String(item[key]), item);
  }
  return out;
}

/** Detailed series info — fetched on-demand when a user opens a series. */
export async function fetchSeriesInfo(creds, seriesId, fetchFn) {
  if (!creds || !seriesId) return null;
  return await call(creds, "get_series_info", { series_id: seriesId }, fetchFn);
}

/**
 * Per-series episode fetcher (lazy / on-demand).
 *
 * Returns a NORMALISED `{ episodes, info, seasons }` payload where
 * `episodes` is a flat array shaped like the rest of the player
 * expects:
 *
 *     [{ season, episode, name, stream_url, container_extension,
 *        added_ts, info, episode_id }, …]
 *
 * `data.episodes` from the Xtream JSON API is an *object* keyed by
 * season number (string), with arrays of episode rows as values. We
 * flatten + normalise here so the calling page doesn't need to know
 * the raw API shape.
 *
 * v1.3.66 — primary fix for the P0 "S2E295–299 missing for Kelders
 * van Geheime" bug. Going through `get_series_info` instead of
 * parsing the M3U bypasses the regex / grouping path that was
 * silently dropping the tail of huge playlists. New episodes added
 * upstream show up on the next series-tile open, no full playlist
 * refresh required.
 */
export async function fetchXtreamSeriesEpisodes(creds, seriesId, fetchFn) {
  if (!creds || !seriesId) return null;
  const data = await call(
    creds,
    "get_series_info",
    { series_id: seriesId },
    fetchFn,
  );
  if (!data) return null;
  const raw = data.episodes || {};
  const out = [];
  for (const [seasonKey, list] of Object.entries(raw)) {
    if (!Array.isArray(list)) continue;
    for (const ep of list) {
      if (!ep || ep.id == null) continue;
      const seasonNum = Number(ep.season) || Number(seasonKey) || 0;
      const epNum = Number(ep.episode_num) || 0;
      const ext = ep.container_extension || "mp4";
      const epInfo = ep.info && typeof ep.info === "object" ? ep.info : {};
      out.push({
        // Keep both the raw provider id (string) AND a normalised
        // `episode_id` so the player + watched-tracker can identify
        // episodes across catalog refreshes.
        episode_id: String(ep.id),
        season: seasonNum,
        episode: epNum,
        name: ep.title || `S${seasonNum} E${epNum}`,
        stream_url: buildSeriesStreamUrl(creds, ep),
        container_extension: ext,
        // Provider-side timestamps when present — exposed so the
        // "Recently added episodes" rail can sort correctly.
        added_ts: Number(ep.added) || Number(epInfo.releasedate) || 0,
        info: epInfo,
        plot: epInfo.plot || null,
        rating: epInfo.rating || null,
        duration: epInfo.duration || null,
        logo_url:
          epInfo.movie_image || epInfo.cover_big || epInfo.cover || null,
      });
    }
  }
  // Stable sort by (season, episode) — matches the order used by
  // Browse.jsx's drill-down view.
  out.sort(
    (a, b) => (a.season - b.season) || (a.episode - b.episode),
  );
  return {
    episodes: out,
    info: data.info || null,
    seasons: Array.isArray(data.seasons) ? data.seasons : [],
  };
}

/** Detailed VOD info — synopsis, cast, runtime, rating. */
export async function fetchVodInfo(creds, vodId, fetchFn) {
  if (!creds || !vodId) return null;
  return await call(creds, "get_vod_info", { vod_id: vodId }, fetchFn);
}

// ============================================================
// Phase 2 (v1.3.66) — Movies via Xtream JSON API
// ============================================================
// ITVZA-style. We never parse the M3U for VOD items when Xtream
// creds are available — instead we hit:
//
//   /player_api.php?action=get_vod_categories
//   /player_api.php?action=get_vod_streams[&category_id=N]
//   /player_api.php?action=get_vod_info&vod_id=N
//
// Wins over M3U parsing:
//   • ~70% smaller payload (JSON arrays vs repeated EXTINF lines)
//   • Server-side categorisation → no fuzzy grouping client-side
//   • Per-category lazy fetch keeps cold-start memory flat
//   • Movies added upstream show up on the next category open,
//     no full playlist refresh required (mirrors the Phase 1
//     fix for series tail-drop).

/** Fetch movie categories. Returns a normalised array
 *  `[{ category_id, category_name, parent_id }, …]`. */
export async function fetchXtreamVodCategories(creds, fetchFn) {
  if (!creds) return null;
  const data = await call(creds, "get_vod_categories", {}, fetchFn);
  if (!Array.isArray(data)) return null;
  return data.map((c) => ({
    category_id: String(c.category_id ?? ""),
    category_name: String(c.category_name ?? "Uncategorized"),
    parent_id: c.parent_id != null ? Number(c.parent_id) : 0,
  }));
}

/**
 * Fetch movies for one category (or all when categoryId is omitted).
 * Returns a NORMALISED array of channel-shaped rows so Browse.jsx
 * can render them with no extra mapping:
 *
 *     [{ name, content_type:"movie", stream_url, stream_id,
 *        category_id, group, logo_url, rating, year, added_ts,
 *        container_extension, plot, xc_kind:"movie", xc_id }, …]
 *
 * Filtering by `category_id` lets the player do a per-category lazy
 * load — for big providers (50k+ movies) this drops the cold-start
 * payload from ~12 MB to ~80 KB per opened category.
 *
 * If `categoryName` is supplied (typically resolved from
 * `fetchXtreamVodCategories`), each row's `group` field will be set
 * to that name so downstream UI (MediaInfoModal, Search results)
 * shows the human-readable category instead of a numeric id.
 */
export async function fetchXtreamVodStreams(creds, categoryId, fetchFn, categoryName) {
  if (!creds) return null;
  const extra = {};
  if (categoryId != null && String(categoryId) !== "") {
    extra.category_id = String(categoryId);
  }
  const data = await call(creds, "get_vod_streams", extra, fetchFn);
  if (!Array.isArray(data)) return null;
  const groupLabel = categoryName || (categoryId != null ? String(categoryId) : "");
  return data
    .filter((m) => m && m.stream_id != null)
    .map((m) => {
      const ext = m.container_extension || "mp4";
      const ratingNum = Number(m.rating_5based ?? m.rating);
      return {
        // Keep raw provider stream_id for stream URL + watched
        // tracking + on-demand `get_vod_info` lookups.
        stream_id: String(m.stream_id),
        xc_kind: "movie",
        xc_id: String(m.stream_id),
        name: m.name || m.title || `Movie ${m.stream_id}`,
        content_type: "movie",
        stream_url: buildVodStreamUrl(creds, m.stream_id, ext),
        container_extension: ext,
        category_id: m.category_id != null ? String(m.category_id) : "",
        // `group` is the user-visible category label. When the caller
        // resolved the category name we surface it here so search /
        // media-info widgets show "Action" instead of "42". Falling
        // back to the id string keeps IDB compound indexes safe
        // (undefined keys silently drop rows).
        group: groupLabel,
        logo_url: m.stream_icon || m.cover_big || m.cover || null,
        rating: Number.isFinite(ratingNum) && ratingNum > 0 ? ratingNum : null,
        year: m.year || null,
        added_ts: Number(m.added) || 0,
        plot: m.plot || null,
        // Trailer + tmdb_id when the provider exposes them — used
        // later by MediaInfoModal to skip the TMDB round-trip.
        tmdb_id: m.tmdb_id || m.tmdb || null,
      };
    });
}

/**
 * Fetch detailed VOD metadata (synopsis, cast, runtime, rating,
 * backdrop) for one movie. Normalised so MediaInfoModal can render
 * directly without parsing the raw `{ info, movie_data }` shape.
 *
 * Returns null when the upstream JSON is missing or malformed.
 */
export async function fetchXtreamVodInfoNormalized(creds, vodId, fetchFn) {
  if (!creds || !vodId) return null;
  const raw = await call(creds, "get_vod_info", { vod_id: vodId }, fetchFn);
  if (!raw || (typeof raw !== "object")) return null;
  const info = raw.info && typeof raw.info === "object" ? raw.info : {};
  const md = raw.movie_data && typeof raw.movie_data === "object" ? raw.movie_data : {};
  const ext = md.container_extension || info.container_extension || "mp4";
  return {
    vod_id: String(vodId),
    name: md.name || info.name || info.title || null,
    plot: info.plot || info.description || null,
    cast: info.cast || null,
    director: info.director || null,
    genre: info.genre || null,
    release_date: info.releasedate || info.releaseDate || null,
    year: (info.releasedate || "").slice(0, 4) || null,
    duration: info.duration || info.duration_secs || null,
    rating: info.rating || md.rating || null,
    backdrop_path:
      Array.isArray(info.backdrop_path) && info.backdrop_path.length
        ? info.backdrop_path[0]
        : info.backdrop_path || null,
    poster: info.movie_image || info.cover_big || md.stream_icon || null,
    tmdb_id: info.tmdb_id || info.tmdb || null,
    youtube_trailer: info.youtube_trailer || null,
    container_extension: ext,
    stream_url: buildVodStreamUrl(creds, vodId, ext),
  };
}

// ============================================================
// Phase 3 (v1.3.66) — Live TV via Xtream JSON API
// ============================================================
// ITVZA-style. When Xtream creds are available we hit:
//
//   /player_api.php?action=get_live_categories
//   /player_api.php?action=get_live_streams[&category_id=N]
//   /player_api.php?action=get_short_epg&stream_id=N[&limit=K]
//
// The first two mirror Phase 2 (Movies). `get_short_epg` is a tiny
// per-channel EPG snapshot used to fill the now/next labels when
// the user opens a channel — way cheaper than re-parsing a multi-MB
// XMLTV file just to learn what's on right now.

/** Fetch Live TV categories. Returns
 *  `[{category_id, category_name, parent_id}, …]`. */
export async function fetchXtreamLiveCategories(creds, fetchFn) {
  if (!creds) return null;
  const data = await call(creds, "get_live_categories", {}, fetchFn);
  if (!Array.isArray(data)) return null;
  return data.map((c) => ({
    category_id: String(c.category_id ?? ""),
    category_name: String(c.category_name ?? "Uncategorized"),
    parent_id: c.parent_id != null ? Number(c.parent_id) : 0,
  }));
}

/**
 * Fetch Live TV channels for one category (or all when categoryId
 * is omitted). Returns a NORMALISED array shaped EXACTLY like the
 * Live TV rows produced by the M3U-enrichment path in
 * `enrichChannelsWithXtream` (see lines 502-516 of this file) so
 * LiveTv.jsx + Browse.jsx + CatchUp can render with zero extra
 * mapping:
 *
 *     [{ id, name, content_type:"live_tv", stream_url, logo_url,
 *        group, xc_kind:"live", xc_id, xc_category_id, category_id,
 *        epg_channel_id, tv_archive, tv_archive_duration }, …]
 *
 * `categoryName` (optional) becomes the row's `group` label so
 * downstream search / favourites widgets show "Sports" instead of
 * "42". Falls back to the id string when not supplied to keep IDB
 * compound indexes safe.
 */
export async function fetchXtreamLiveStreams(creds, categoryId, fetchFn, categoryName) {
  if (!creds) return null;
  const extra = {};
  if (categoryId != null && String(categoryId) !== "") {
    extra.category_id = String(categoryId);
  }
  const data = await call(creds, "get_live_streams", extra, fetchFn);
  if (!Array.isArray(data)) return null;
  const groupLabel = categoryName || (categoryId != null ? String(categoryId) : "");
  return data
    .filter((l) => l && l.stream_id != null)
    .map((l) => {
      const id = String(l.stream_id);
      const archiveOn = l.tv_archive === 1 || l.tv_archive === "1";
      return {
        // `id` mirrors the M3U-built shape — LiveTv.jsx finds the
        // selected channel via `c.id === selected.id`.
        id: `xc-live-${id}`,
        stream_id: id,
        name: l.name || `Channel ${id}`,
        content_type: "live_tv",
        stream_url: buildLiveStreamUrl(creds, id),
        logo_url: l.stream_icon || null,
        group: groupLabel,
        xc_kind: "live",
        xc_id: id,
        xc_category_id: l.category_id != null ? String(l.category_id) : null,
        category_id: l.category_id != null ? String(l.category_id) : "",
        // Carry the EPG channel id through so currentProgramme() / EPG
        // grids can match this entry against the XMLTV catalog.
        epg_channel_id: l.epg_channel_id || null,
        // Catch-up flags — Phase 4 will use these to surface the
        // catch-up rail per-channel without a full M3U re-parse.
        tv_archive: archiveOn,
        tv_archive_duration:
          parseInt(l.tv_archive_duration, 10) || (archiveOn ? 7 : 0),
        // Sort hint for the channel-number column.
        num: Number(l.num) || null,
        added_ts: Number(l.added) || 0,
      };
    });
}

/**
 * Short / now+next EPG for one Live TV channel. Cheap, narrow
 * payload (typically 2 programmes) — used when the user opens a
 * channel without the full XMLTV catalog loaded.
 *
 * Returns `[{title, description, start_ts, stop_ts}, …]` sorted
 * chronologically. Returns null on failure.
 */
export async function fetchXtreamShortEpg(creds, streamId, fetchFn, limit = 4) {
  if (!creds || !streamId) return null;
  const raw = await call(
    creds,
    "get_short_epg",
    { stream_id: String(streamId), limit: String(limit) },
    fetchFn,
  );
  if (!raw || !Array.isArray(raw.epg_listings)) return null;
  return raw.epg_listings
    .filter((p) => p && p.start_timestamp != null)
    .map((p) => {
      // Xtream returns base64-encoded title/description.
      let title = p.title || "";
      let desc = p.description || "";
      try { title = _b64Decode(title); } catch { /* leave as-is */ }
      try { desc = _b64Decode(desc); } catch { /* leave as-is */ }
      return {
        title,
        description: desc,
        // Both timestamps are seconds since epoch as strings — normalise to ms.
        start_ts: Number(p.start_timestamp) * 1000,
        stop_ts: Number(p.stop_timestamp) * 1000,
      };
    })
    .sort((a, b) => a.start_ts - b.start_ts);
}

// Tiny base64 decoder that works in browser + Node test env (Jest
// uses jsdom which exposes atob). Wrapped because some panels
// return non-encoded strings (already plain ASCII) and we shouldn't
// blow up on those.
function _b64Decode(s) {
  if (!s) return "";
  if (typeof atob === "function") {
    try { return decodeURIComponent(escape(atob(s))); } catch { /* fall through */ }
  }
  return s;
}

/** Build the playable stream URL for a VOD item. */
export function buildVodStreamUrl(creds, vodId, containerExtension = "mp4") {
  return `${creds.streamBase}/movie/${creds.username}/${creds.password}/${vodId}.${containerExtension}`;
}

/** Build the playable stream URL for a series episode. */
export function buildSeriesStreamUrl(creds, episode) {
  const ext = episode.container_extension || "mp4";
  return `${creds.streamBase}/series/${creds.username}/${creds.password}/${episode.id}.${ext}`;
}

/** Build the playable HLS URL for a live channel. */
export function buildLiveStreamUrl(creds, streamId) {
  return `${creds.streamBase}/live/${creds.username}/${creds.password}/${streamId}.m3u8`;
}

/**
 * Merge Xtream catalog data into the parsed-M3U channel list so the rest
 * of the player (Browse, CatchUp, Search) sees one unified shape.
 *
 * Strategy: identify each item in the M3U by its stream URL → match it to
 * the Xtream entry that produced that URL → replace `logo_url`, add `year`,
 * `rating`, `plot`, `xc_*` ids so info pages can fetch detail later.
 */
export async function enrichChannelsWithXtream(channels, catalog, creds, opts = {}) {
  if (!catalog || !creds) return channels;
  // v1.4.26 — admin toggle for huge M3U catalogs (400k+ rows). When
  // the per-playlist `disable_series_preload` flag is on, we skip the
  // series-harvest pass below. Users still see all M3U-embedded series
  // rows; the only thing lost is the Xtream `get_series` catalog that
  // adds series NOT present in the M3U. Trade-off: fewer OOM crashes
  // on Smart TVs at the cost of a slightly smaller series library.
  const skipSeriesHarvest = !!opts.skipSeriesHarvest;

  // Build lookups by stream_id keyed off the URL path.
  const vodById = new Map(catalog.vodStreams.map((v) => [String(v.stream_id), v]));
  const liveById = new Map(catalog.liveStreams.map((l) => [String(l.stream_id), l]));
  // Series matching:
  //   1. Build the strict normalised-name → series map.
  //   2. Also build a "token set" view for fuzzy fallback matches when the
  //      M3U title doesn't normalise to an exact catalog name.
  const seriesByName = new Map(
    catalog.seriesList.map((s) => [normaliseTitle(s.name), s])
  );
  const seriesEntries = catalog.seriesList.map((s) => ({
    s,
    norm: normaliseTitle(s.name),
  }));

  const fuzzyFindSeries = (m3uName) => {
    const norm = normaliseTitle(m3uName);
    if (!norm) return null;
    // 2a. Exact normalised hit (fast path)
    if (seriesByName.has(norm)) return seriesByName.get(norm);
    // 2b. Prefix / contains in either direction
    for (const { s, norm: n } of seriesEntries) {
      if (!n) continue;
      if (norm.startsWith(n) || n.startsWith(norm)) return s;
    }
    for (const { s, norm: n } of seriesEntries) {
      if (!n) continue;
      if (norm.includes(n) || n.includes(norm)) return s;
    }
    return null;
  };

  // v1.3.47 — memoize fuzzyFindSeries.
  // The previous build walked the full catalog (15k+ series) twice per
  // M3U series row. With 50k+ series episodes that's ~750 million
  // string comparisons → ~1m50s of CPU on a Smart TV WebView. Caching
  // by normalised name typically cuts unique calls by 10–20×, so this
  // single change knocks ~80 s off the cold-start playlist update.
  const fuzzyCache = new Map();
  const fuzzyFindSeriesMemo = (m3uName) => {
    const norm = normaliseTitle(m3uName);
    if (!norm) return null;
    if (fuzzyCache.has(norm)) return fuzzyCache.get(norm);
    const found = fuzzyFindSeries(m3uName);
    fuzzyCache.set(norm, found);
    return found;
  };

  // For huge catalogs (500k+ items) we yield to the event loop every N
  // entries so the JS thread doesn't lock up Android's ANR watchdog or
  // drown the Smart TV's renderer in synchronous work.
  const YIELD_EVERY_LOCAL = YIELD_EVERY;
  const _yieldLocal = YIELD_EVERY_LOCAL; // eslint-disable-line no-unused-vars
  const out = new Array(channels.length);
  for (let idx = 0; idx < channels.length; idx++) {
    const ch = channels[idx];
    const id = extractStreamId(ch.stream_url);
    if (!id) {
      out[idx] = ch;
      if ((idx + 1) % YIELD_EVERY === 0) await new Promise((r) => setTimeout(r, 0));
      continue;
    }

    if (ch.content_type === "movie" && vodById.has(id)) {
      const v = vodById.get(id);
      const addedRaw = v.added != null ? String(v.added).trim() : "";
      const addedTs = addedRaw ? parseInt(addedRaw, 10) || 0 : 0;
      // Some providers expose the TMDB id at multiple field names — `tmdb`,
      // `tmdb_id`, sometimes nested inside `info`. Grab the first one we
      // find so the modal can hit our /api/tmdb proxy without waiting for
      // `get_vod_info` to finish (faster + works even when the per-item
      // endpoint is missing entirely).
      const tmdbId =
        v.tmdb_id || v.tmdb || v.info?.tmdb_id || v.info?.tmdb || null;
      out[idx] = {
        ...ch,
        logo_url: v.stream_icon || ch.logo_url,
        year: v.year ? parseInt(v.year, 10) : ch.year,
        rating: v.rating || null,
        plot: v.plot || null,
        added_ts: addedTs || ch.added_ts || 0,
        xc_kind: "vod",
        xc_id: id,
        xc_category_id: v.category_id ? String(v.category_id) : null,
        tmdb_id: tmdbId ? String(tmdbId) : null,
        group: ch.group || (v.category_id
          ? catalog.categories.vod.get(String(v.category_id))?.category_name
          : null),
      };
    } else if (ch.content_type === "live_tv" && liveById.has(id)) {
      const l = liveById.get(id);
      const archiveOn = l.tv_archive === 1 || l.tv_archive === "1";
      out[idx] = {
        ...ch,
        logo_url: l.stream_icon || ch.logo_url,
        xc_kind: "live",
        xc_id: id,
        xc_category_id: l.category_id ? String(l.category_id) : null,
        // Carry catch-up flags forward so the CatchUp page can filter to
        // channels that actually have an archive (matches vupro's behaviour).
        tv_archive: archiveOn,
        tv_archive_duration:
          parseInt(l.tv_archive_duration, 10) || (archiveOn ? 7 : 0),
      };
    } else if (ch.content_type === "series") {
      const series = fuzzyFindSeriesMemo(ch.series_name || ch.name);
      if (series) {
        const lmRaw = series.last_modified != null ? String(series.last_modified).trim() : "";
        const lastModifiedTs = lmRaw ? parseInt(lmRaw, 10) || 0 : 0;
        // Same TMDB-id rescue as VOD — providers stash it under a few
        // different keys, capture whichever exists.
        const tmdbId =
          series.tmdb_id ||
          series.tmdb ||
          series.info?.tmdb_id ||
          series.info?.tmdb ||
          null;
        out[idx] = {
          ...ch,
          logo_url: pickPoster(series) || ch.logo_url,
          year: series.year ? parseInt(series.year, 10) : ch.year,
          rating: series.rating || null,
          plot: series.plot || null,
          series_last_modified_ts: lastModifiedTs || ch.series_last_modified_ts || 0,
          xc_kind: "series",
          xc_id: String(series.series_id),
          xc_category_id: series.category_id ? String(series.category_id) : null,
          tmdb_id: tmdbId ? String(tmdbId) : null,
          group: ch.group || (series.category_id
            ? catalog.categories.series.get(String(series.category_id))?.category_name
            : null),
        };
      } else {
        out[idx] = ch;
      }
    } else {
      out[idx] = ch;
    }

    if ((idx + 1) % YIELD_EVERY === 0) await new Promise((r) => setTimeout(r, 0));
  }

  // v1.4.27 — Harvest of Xtream-only Live / VOD / Series entries used
  // to happen inline here. It's now been split into a separate export
  // (`harvestXtreamExtras`) so `loadPlaylist` can:
  //   1. return the fast M3U-based `out` first,
  //   2. render the main menu with what we have,
  //   3. run the harvest in the background,
  //   4. emit a `vupp:playlist-augmented` window event when done.
  // See harvestXtreamExtras() below and playlistCache.js `loadPlaylist`
  // for the wiring.
  return out;
}


/**
 * Background pass that adds every Xtream Live / VOD / Series entry not
 * already represented in `channels` (the output of
 * `enrichChannelsWithXtream`). Runs AFTER the main menu is rendered so
 * the initial paint stays fast on 400k-row catalogs; new rows are
 * merged into the channels store + broadcast via
 * `vupp:playlist-augmented` so consumers can refresh their counts
 * silently.
 *
 * Returns the NEW rows only — the caller decides how to merge them.
 */
export async function harvestXtreamExtras(channels, catalog, creds, opts = {}) {
  if (!catalog || !creds) return [];
  const skipSeriesHarvest = !!opts.skipSeriesHarvest;

  const havingSeriesId = new Set();
  const havingLive = new Set();
  const havingVod = new Set();
  for (const ch of channels) {
    if (ch.content_type === "series" && ch.xc_id) {
      havingSeriesId.add(String(ch.xc_id));
    } else if (ch.content_type === "live_tv" && ch.xc_id) {
      havingLive.add(String(ch.xc_id));
    } else if (ch.content_type === "movie" && ch.xc_id) {
      havingVod.add(String(ch.xc_id));
    }
  }

  const baseHost = String(
    creds?.streamBase || creds?.host || creds?.server || "",
  ).replace(/\/+$/, "");
  const user = creds?.username || "";
  const pass = creds?.password || "";
  const canBuildUrl = !!baseHost && !!user && !!pass;
  const added = [];

  // ─── Series (respects the admin toggle) ────────────────────────
  if (!skipSeriesHarvest) {
    const seriesArr = catalog.seriesList || [];
    for (let i = 0; i < seriesArr.length; i++) {
      const s = seriesArr[i];
      const sid = String(s?.series_id ?? "");
      if (!sid || havingSeriesId.has(sid)) {
        if ((i + 1) % YIELD_EVERY === 0) await new Promise((r) => setTimeout(r, 0));
        continue;
      }
      const lmRaw = s.last_modified != null ? String(s.last_modified).trim() : "";
      const lastModifiedTs = lmRaw ? parseInt(lmRaw, 10) || 0 : 0;
      const cat = s.category_id
        ? catalog.categories.series.get(String(s.category_id))
        : null;
      const tmdbId =
        s.tmdb_id || s.tmdb || s.info?.tmdb_id || s.info?.tmdb || null;
      added.push({
        name: s.name || `Series ${sid}`,
        content_type: "series",
        stream_url: `xtream://series/${sid}`,
        logo_url: pickPoster(s) || null,
        group: cat?.category_name || "Series",
        group_title: cat?.category_name || "Series",
        year: s.year ? parseInt(s.year, 10) : null,
        rating: s.rating || null,
        plot: s.plot || null,
        series_last_modified_ts: lastModifiedTs,
        xc_kind: "series",
        xc_id: sid,
        xc_category_id: s.category_id ? String(s.category_id) : null,
        tmdb_id: tmdbId ? String(tmdbId) : null,
        tvg_id: null,
        epg_channel_id: null,
      });
      havingSeriesId.add(sid);
      if ((i + 1) % YIELD_EVERY === 0) await new Promise((r) => setTimeout(r, 0));
    }
  }

  // ─── Live TV ───────────────────────────────────────────────────
  if (canBuildUrl) {
    const liveArr = catalog.liveStreams || [];
    for (let i = 0; i < liveArr.length; i++) {
      const l = liveArr[i];
      const sid = String(l?.stream_id ?? "");
      if (!sid || havingLive.has(sid)) {
        if ((i + 1) % YIELD_EVERY === 0) await new Promise((r) => setTimeout(r, 0));
        continue;
      }
      const cat = l.category_id
        ? catalog.categories.live.get(String(l.category_id))
        : null;
      const archiveOn = l.tv_archive === 1 || l.tv_archive === "1";
      added.push({
        name: l.name || `Channel ${sid}`,
        content_type: "live_tv",
        stream_url: `${baseHost}/live/${user}/${pass}/${sid}.m3u8`,
        logo_url: l.stream_icon || null,
        group: cat?.category_name || "Live TV",
        group_title: cat?.category_name || "Live TV",
        xc_kind: "live",
        xc_id: sid,
        xc_category_id: l.category_id ? String(l.category_id) : null,
        tv_archive: archiveOn,
        tv_archive_duration:
          parseInt(l.tv_archive_duration, 10) || (archiveOn ? 7 : 0),
        tvg_id: l.epg_channel_id || null,
        epg_channel_id: l.epg_channel_id || null,
      });
      havingLive.add(sid);
      if ((i + 1) % YIELD_EVERY === 0) await new Promise((r) => setTimeout(r, 0));
    }
  }

  // ─── VOD ───────────────────────────────────────────────────────
  if (canBuildUrl) {
    const vodArr = catalog.vodStreams || [];
    for (let i = 0; i < vodArr.length; i++) {
      const v = vodArr[i];
      const sid = String(v?.stream_id ?? "");
      if (!sid || havingVod.has(sid)) {
        if ((i + 1) % YIELD_EVERY === 0) await new Promise((r) => setTimeout(r, 0));
        continue;
      }
      const ext = v.container_extension || "mp4";
      const cat = v.category_id
        ? catalog.categories.vod.get(String(v.category_id))
        : null;
      const addedRaw = v.added != null ? String(v.added).trim() : "";
      const addedTs = addedRaw ? parseInt(addedRaw, 10) || 0 : 0;
      const tmdbId =
        v.tmdb_id || v.tmdb || v.info?.tmdb_id || v.info?.tmdb || null;
      added.push({
        name: v.name || `Movie ${sid}`,
        content_type: "movie",
        stream_url: `${baseHost}/movie/${user}/${pass}/${sid}.${ext}`,
        logo_url: v.stream_icon || null,
        group: cat?.category_name || "Movies",
        group_title: cat?.category_name || "Movies",
        year: v.year ? parseInt(v.year, 10) : null,
        rating: v.rating || null,
        plot: v.plot || null,
        added_ts: addedTs,
        xc_kind: "vod",
        xc_id: sid,
        xc_category_id: v.category_id ? String(v.category_id) : null,
        tmdb_id: tmdbId ? String(tmdbId) : null,
        tvg_id: null,
        epg_channel_id: null,
      });
      havingVod.add(sid);
      if ((i + 1) % YIELD_EVERY === 0) await new Promise((r) => setTimeout(r, 0));
    }
  }

  const counts = added.reduce(
    (a, c) => {
      a[c.content_type] = (a[c.content_type] || 0) + 1;
      return a;
    },
    {},
  );
  try {
    // eslint-disable-next-line no-console
    console.info(
      `[xtream] Background harvest added ${added.length} row(s): live=${counts.live_tv || 0}, vod=${counts.movie || 0}, series=${counts.series || 0}`,
    );
  } catch {/* noop */}
  return added;
}

function normaliseTitle(s) {
  return (s || "")
    .toLowerCase()
    // Strip year suffixes like (2022), [2022], or " 2022"
    .replace(/[([{]?\b(19|20)\d{2}\b[)\]}]?/g, " ")
    // Strip common quality / encoding tags that appear in IPTV M3U titles
    .replace(
      /\b(hd|fhd|uhd|4k|hevc|h\.?264|h\.?265|x264|x265|1080p?|720p?|480p?|webrip|webdl|web-dl|bluray|brrip|dvdrip|extended|directors? cut|hindi|english|tamil|telugu|dubbed|subbed)\b/gi,
      " "
    )
    .replace(/[^a-z0-9]+/g, "");
}

function pickPoster(s) {
  // Different Xtream panels populate different cover fields — try them all.
  //
  // v1.4.27 — Added `movie_image`, `series_cover`, `series_image`,
  // `poster_url`, `image_url`, `image` and the nested `s.info.*` variants.
  // Some providers (notably the SA panels the customer uses) leave
  // the top-level `cover` empty but populate `movie_image` or
  // `series_image` — matching what vupro.apk does. That's why some
  // series had covers in vupro but the TV placeholder in ZA Player Pro.
  if (!s) return null;
  const info = s.info || {};
  const candidates = [
    s.cover,
    s.cover_big,
    s.poster,
    s.movie_image,
    s.series_cover,
    s.series_image,
    s.poster_url,
    s.image_url,
    s.image,
    Array.isArray(s.backdrop_path) ? s.backdrop_path[0] : null,
    s.stream_icon,
    info.cover,
    info.cover_big,
    info.movie_image,
    info.poster,
    info.image,
    Array.isArray(info.backdrop_path) ? info.backdrop_path[0] : null,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim() && c !== "null" && c !== "undefined") {
      return c.trim();
    }
  }
  return null;
}

function extractStreamId(url) {
  // Xtream stream URLs look like:
  //   .../live/USER/PASS/12345.m3u8
  //   .../movie/USER/PASS/67890.mp4
  //   .../series/USER/PASS/24681.mkv
  const m = url.match(/\/(live|movie|series)\/[^/]+\/[^/]+\/(\d+)(?:\.[a-z0-9]+)?$/i);
  return m ? m[2] : null;
}

/**
 * Fetch the catch-up programme table for a single Xtream live channel.
 * Mirrors what vupro.apk does (`action=get_simple_data_table`). Returns
 * `epg_listings` — an array of past + future programmes for that channel
 * with the unix timestamps the catchup URL builder needs.
 */
export async function fetchXtreamCatchupTable(creds, streamId, fetchFn) {
  if (!creds || !streamId) return [];
  const r = await call(
    creds,
    "get_simple_data_table",
    { stream_id: streamId },
    fetchFn
  );
  return (r && Array.isArray(r.epg_listings)) ? r.epg_listings : [];
}

/**
 * Build Xtream catch-up stream URLs.
 *
 * Reference apps:
 *   • IPTV Smarters     → `/timeshift/USER/PASS/MIN/yyyy-MM-dd:HH-mm/ID.ts`
 *   • MaxPlayer / vupro → `/streaming/timeshift.php?username=…&password=…&stream=…&start=…&duration=…`
 *
 * Both use the device's LOCAL timezone for the timestamp (DEX-confirmed for
 * Smarters; web-bundle confirmed for MaxPlayer). We emit the PHP endpoint
 * as `primary` (works on every panel — no mod_rewrite needed) and the .ts
 * rewrite as `fallback` for panels that prefer that style.
 *
 *  primary  → /streaming/timeshift.php?username=…&password=…&stream=…&start=…&duration=…
 *  fallback → /timeshift/USER/PASS/MIN/YYYY-MM-DD:HH-MM/STREAM_ID.ts
 */
export function buildXtreamCatchupUrl(creds, streamId, startTs, durationSeconds) {
  if (!creds || !streamId) return null;
  // Local time (matches MaxPlayer + Smarters).
  const d = new Date(startTs * 1000);
  const pad = (n) => String(n).padStart(2, "0");
  const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}:${pad(d.getHours())}-${pad(d.getMinutes())}`;
  const minutes = Math.max(1, Math.round(durationSeconds / 60));
  const u = encodeURIComponent(creds.username);
  const p = encodeURIComponent(creds.password);

  // v1.4.42 — Prefer the /timeshift/… rewrite (used by IPTV Smarters /
  // MaxPlayer / VuPro and supported by nearly every modern panel).
  // Fall back to /streaming/timeshift.php if the rewrite 404s.
  const primary = `${creds.streamBase}/timeshift/${creds.username}/${creds.password}/${minutes}/${stamp}/${streamId}.ts`;

  const fallback =
    `${creds.streamBase}/streaming/timeshift.php` +
    `?username=${u}&password=${p}` +
    `&stream=${streamId}&start=${encodeURIComponent(stamp)}&duration=${minutes}`;

  return { primary, fallback };
}


/**
 * Fetch the "most watched" stream stats from a 1-Stream-style panel.
 *
 * Endpoint (provider-side, NOT player_api.php):
 *   `${streamBase}/play/b2c/v1/statistics/streams/most_watched`
 *
 * Auth defaults to the same `?username=&password=` pattern Xtream uses for
 * everything else. The endpoint is panel-specific — it's a 404 on classic
 * Xtream panels — so we treat any non-2xx / non-JSON response as "feature
 * unavailable" and return an empty array. The caller is expected to render
 * a graceful empty state.
 *
 * Returns an array (whatever shape the panel uses). Caller normalises.
 */
export async function fetchMostWatched(creds, fetchFn) {
  if (!creds) return [];
  const u = encodeURIComponent(creds.username);
  const p = encodeURIComponent(creds.password);
  const url =
    `${creds.streamBase}/play/b2c/v1/statistics/streams/most_watched` +
    `?username=${u}&password=${p}`;
  try {
    const text = fetchFn ? await fetchFn(url) : await (await webFetch(url)).text();
    if (!text || text[0] !== "[" && text[0] !== "{") return [];
    const data = JSON.parse(text);
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.streams)) return data.streams;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  } catch {
    return [];
  }
}
